import React, { useEffect, useState } from 'react'
import useTitle from 'utils/customHook/useTitle'
import i18n from 'simple-react-i18n'
import { useParams } from 'react-router'
import EntityPanel from './EntityPanel'
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material'
import { maxBy } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import UpdateEntityPanel from './UpdateEntityPanel'
import UpdatePanel from 'components/updatePanel/UpdatePanel'
import useActions from 'utils/customHook/useActions'
import RPQSBreadCrumb from '../RPQSBreadCrumb'
import { FINANCIAL_COLOR, NETWORK_COLOR, QUALITY_COLOR, SUBSCRIBER_COLOR } from 'rpqs/constants/RpqsConstant'
import IndicatorCategoryCard from '../IndicatorCategoryCard'
import RpqsAction from 'rpqs/actions/RpqsAction'
import { push } from '@lagunovsky/redux-react-router'
import { PATH_RPQS } from 'home/constants/RouteConstants'
import RpqsEditPanel from '../dashboard/RpqsEditPanel'
import ToastrAction from '../../../toastr/actions/ToastrAction'
import DialogDuplicateRPQS from '../dashboard/DialogDuplicateRPQS'
import ProgressCard from '../../../components/card/ProgressCard'
import { isRpqsLoaded } from '../../utils/RPQSUtils'
import RpqsNotesPanel from '../dashboard/RpqsNotesPanel'
import RpqsLastEventPanel from '../dashboard/RpqsLastEventPanel'
import RpqsFilesPanel from '../dashboard/RpqsFilesPanel'
import RpqsConformityPanel from './RpqsConformityPanel'

const RpqsDashboardApp = () => {
    const {
        rpqsProps,
        collectivity,
        managementUnits,
        rpqsList,
    } = useSelector(store => ({
        rpqsProps: store.RpqsReducer.rpqs,
        collectivity: store.RpqsReducer.collectivity,
        managementUnits: store.RpqsReducer.managementUnits,
        rpqsList: store.RpqsReducer.rpqsList,
    }), shallowEqual)

    const dispatch = useDispatch()
    const { annee, idCollectivite } = useParams()

    useEffect(() => {
        dispatch(RpqsAction.fetchAllManagementUnits())
        dispatch(RpqsAction.fetchAllRpqs())
    }, [dispatch])

    const isLoaded = isRpqsLoaded(rpqsProps, annee, idCollectivite) && collectivity?.id === parseInt(idCollectivite)

    const [rpqs, setRpqs] = useState({})
    const [readMode, setReadMode] = useState(true)
    const [openDialogDuplicate, setOpenDialogDuplicate] = useState(false)

    const year = rpqsProps.year || `${i18n.year} ${i18n.unknown}`

    useEffect(() => {
        if (isLoaded) {
            setRpqs(rpqsProps)
        }
    }, [rpqsProps, isLoaded])

    useTitle(() => [{
        title: i18n.rpqs,
        href: 'rpqs',
    }, {
        title: year,
        href: 'rpqs',
    }, {
        title: rpqsProps.name || i18n.unknown,
        href: `rpqs/${annee}/collectivite/${idCollectivite}/dashboard`,
    }], [rpqsProps])

    useActions(() => {
        if (readMode) {
            return {
                delete: () => {
                    dispatch(RpqsAction.deleteRpqs(annee, idCollectivite)).then(() => dispatch(push(PATH_RPQS)))
                },
                edit: () => setReadMode(false),
                export: () => {},
                duplicate: () => setOpenDialogDuplicate(true),
            }
        }
        return {
            cancel: () => {
                setRpqs(rpqsProps)
                setReadMode(true)
            },
            save: () => {
                if (!rpqs.name) {
                    dispatch(ToastrAction.error(i18n.allFieldsAreRequired))
                    return
                }
                dispatch(RpqsAction.updateRpqs(rpqs)).then(json => {
                    if (json?.update) setReadMode(true)
                })
            },
        }
    }, [readMode, rpqs, rpqsProps, annee, idCollectivite])

    const lastUpadatedEntity = collectivity?.managementEntities && maxBy(collectivity.managementEntities, 'updateDate')
    const managementUnitsFiltered = managementUnits.filter(m => collectivity.link_managementUnits?.some(l => l.managementUnitCode === m.id))

    // Années déjà déclarées pour la collectivité (exclues du choix de duplication).
    const existingYears = rpqsList.filter(r => r.collectivityId === rpqsProps.collectivityId).map(r => r.year)

    const cards = [
        {
            title: i18n.subscriber,
            advencement: 35,
            status: 0,
            nbIndicators: 11,
            nbIndicatorsTotal: 33,
            color: SUBSCRIBER_COLOR,
        },
        {
            title: i18n.network,
            advencement: 35,
            status: 0,
            nbIndicators: 11,
            nbIndicatorsTotal: 33,
            color: NETWORK_COLOR,
        },
        {
            title: i18n.quality,
            advencement: 0,
            status: 0,
            nbIndicators: 33,
            nbIndicatorsTotal: 33,
            color: QUALITY_COLOR,
        },
        {
            title: i18n.financial,
            advencement: 100,
            status: 1,
            color: FINANCIAL_COLOR,
        },
    ]

    if (!isLoaded) {
        return <ProgressCard indeterminate round />
    }

    return (
        <Grid
            container
            columnSpacing={1}
            sx={{ padding: 1 }}
        >
            <Grid size={9}>
                {!readMode && (
                    <Box sx={{ paddingBottom: 1 }}>
                        <RpqsEditPanel rpqs={rpqs} onChange={changes => setRpqs(prev => ({ ...prev, ...changes }))} />
                    </Box>
                )}
                <Card>
                    <CardContent>
                        <Stack spacing={2}>
                            <Typography variant='title'>{rpqs.name}</Typography>
                            <RPQSBreadCrumb />
                            <Grid container columnSpacing={1}>
                                {cards.map(card => (
                                    <IndicatorCategoryCard
                                        key={card.title}
                                        card={card}
                                    />
                                ))}
                            </Grid>
                            <EntityPanel managementEntities={managementUnitsFiltered} />
                        </Stack>
                    </CardContent>
                </Card>
                <Box sx={{ paddingTop: 1 }}>
                    <RpqsConformityPanel managementEntities={managementUnitsFiltered} year={annee} />
                </Box>
            </Grid>
            <Grid container direction='column' size={3} rowSpacing={1}>
                <UpdatePanel
                    updateLogin={rpqs.updateLogin}
                    updateDate={rpqs.updateDate}
                />
                {lastUpadatedEntity && (
                    <UpdateEntityPanel
                        entityName={lastUpadatedEntity.name}
                        updateLogin={lastUpadatedEntity.updateLogin}
                        updateDate={lastUpadatedEntity.updateDate}
                    />
                )}
                <RpqsNotesPanel
                    notes={rpqs.notes}
                    onChange={notes => setRpqs(prev => ({ ...prev, notes }))}
                    readMode={readMode}
                />
                <RpqsLastEventPanel year={annee} collectivityId={idCollectivite} />
                <RpqsFilesPanel year={annee} collectivityId={idCollectivite} />
            </Grid>
            <DialogDuplicateRPQS
                open={openDialogDuplicate}
                onClose={() => setOpenDialogDuplicate(false)}
                sourceRpqs={rpqs}
                existingYears={existingYears}
            />
        </Grid>
    )
}

export default RpqsDashboardApp